import { derived, readable, type Readable } from 'svelte/store';

import type { DynamicConfigOptions } from '@/teoCore/DynamicConfig';
import { TeoCore } from '@/teoCore/TeoCore';
import { useReactiveState } from '@/utils/reactive-store/svelte/use-reactive-store.svelte';

export function useCoreState() {
  return useReactiveState(TeoCore.instance.state);
}

export function useCoreStateAnalysisUsageQuota() {
  return useReactiveState(TeoCore.instance.state, {
    get: (state) => state.analysisUsageQuota,
  });
}

export function useCoreStateAnalysisUsageQuotaInfo() {
  return derived(useCoreStateAnalysisUsageQuota(), (v) => {
    if (v?.total && v.total <= 0) return null;
    const used = Math.min(v.total, v.used ?? 0);
    return {
      text: `[${v.total - used}/${v.total}]`,
      depleted: used >= v.total,
      total: v.total,
      used: v.used,
    };
  });
}

let dynamicConfigReadable: Maybe<Readable<DynamicConfigOptions>>;

export function useDynamicConfig(): Readable<DynamicConfigOptions> {
  if (!dynamicConfigReadable) {
    dynamicConfigReadable = readable(TeoCore.instance.dynamicConfig.options, (set) => {
      TeoCore.instance.dynamicConfig.addEventListeners({
        alwaysCheckBrandFit: () => set(TeoCore.instance.dynamicConfig.options),
        brandFitEnabled: () => set(TeoCore.instance.dynamicConfig.options),
        evaluationProfileId: () => set(TeoCore.instance.dynamicConfig.options),
        subscriptionName: () => set(TeoCore.instance.dynamicConfig.options),
        textConfigOptions: () => set(TeoCore.instance.dynamicConfig.options),
        textGeneratorEnabled: () => set(TeoCore.instance.dynamicConfig.options),
        useExpParser: () => set(TeoCore.instance.dynamicConfig.options),
        whitelistEnabled: () => set(TeoCore.instance.dynamicConfig.options),
      });
    });
  }
  return dynamicConfigReadable;
}
